import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import { resolveImages } from '../lib/imageBank';

const findProduct = (slug) => PRODUCTS.find((p) => p.slug === slug);

export function ProductCards({ slugs = [], dark = false }) {
  const items = slugs.map(findProduct).filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="chat-cards">
      {items.map((p) => (
        <Link
          key={p.slug}
          to={`/products/${p.slug}`}
          data-testid={`chat-card-${p.slug}`}
          className={`group block rounded-xl overflow-hidden border ${dark ? 'border-white/10 bg-white/[0.03]' : 'border-black/10 bg-white'} transition-colors hover:border-[#C7A36A]`}
        >
          <div className="relative aspect-[4/3] overflow-hidden">
            <img src={p.hero} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>
          <div className="p-3">
            <div className="flex items-baseline justify-between">
              <div>
                <div className={`font-display text-lg tracking-tight ${dark ? 'text-white' : 'text-[#111]'}`}>{p.name}</div>
                <div className={`text-[10px] tracking-[0.24em] uppercase ${dark ? 'text-white/50' : 'text-black/45'} mt-0.5`}>{p.specs.carpetArea}</div>
              </div>
              <ArrowUpRight size={14} className={`${dark ? 'text-white/60' : 'text-black/50'} group-hover:text-[#C7A36A]`} />
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export function CompareTable({ slugs = [], dark = false }) {
  const items = slugs.map(findProduct).filter(Boolean);
  if (items.length < 2) return null;
  const rows = [
    { label: 'Family',      get: (p) => p.family === 'capsule' ? 'Capsule Suites' : 'Studio Suites' },
    { label: 'Length',      get: (p) => p.specs.length },
    { label: 'Width',       get: (p) => p.specs.width },
    { label: 'Height',      get: (p) => p.specs.height },
    { label: 'Carpet Area', get: (p) => p.specs.carpetArea },
    { label: 'Tagline',     get: (p) => p.tagline },
  ];
  const border = dark ? 'border-white/10' : 'border-black/10';
  const muted  = dark ? 'text-white/50' : 'text-black/45';
  const text   = dark ? 'text-white'    : 'text-[#111]';
  return (
    <div className={`mt-4 rounded-xl border ${border} overflow-hidden`} data-testid="chat-compare">
      <div className={`grid`} style={{ gridTemplateColumns: `160px repeat(${items.length}, minmax(0,1fr))` }}>
        <div className={`p-3 text-[10px] tracking-[0.24em] uppercase ${muted}`}>Specification</div>
        {items.map((p) => (
          <Link key={p.slug} to={`/products/${p.slug}`} className={`p-3 font-display text-lg ${text} hover:text-[#C7A36A]`}>{p.name}</Link>
        ))}
        {rows.map((r) => (
          <React.Fragment key={r.label}>
            <div className={`p-3 border-t ${border} text-[11px] tracking-[0.18em] uppercase ${muted}`}>{r.label}</div>
            {items.map((p) => (
              <div key={p.slug + r.label} className={`p-3 border-t ${border} text-sm ${text}`}>{r.get(p)}</div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export function ImageGrid({ keys = [] }) {
  const urls = resolveImages(keys);
  if (!urls.length) return null;
  return (
    <div className="mt-4 grid grid-cols-3 gap-2" data-testid="chat-images">
      {urls.map((u, i) => (
        <div key={u + i} className="relative aspect-square overflow-hidden rounded-lg bg-black/10">
          <img src={u} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        </div>
      ))}
    </div>
  );
}
